import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { SEGMENTS, MODEL, MATRIX } from "@/data/content";
import { Container, ChapterHeader, Section, Reveal, LevelChip, CTAButton } from "@/components/landing/shared";
import { XModelImage } from "@/components/landing/XModelImage";
import { usePrefersReducedMotion, EASE } from "@/lib/motion";
import { scrollToId } from "@/lib/scroll";

/** Continuous, looping strip of the five engagement segments — replaces the static tab list. */
const SegmentMarquee = () => {
  const reduced = usePrefersReducedMotion();
  const items = [...SEGMENTS, ...SEGMENTS];
  return (
    <div className="mask-fade-x mt-3 overflow-hidden" data-testid="segment-marquee" aria-hidden="true">
      <motion.div
        className="flex w-max items-center gap-6 whitespace-nowrap"
        animate={reduced ? {} : { x: ["0%", "-50%"] }}
        transition={reduced ? {} : { duration: 18, ease: "linear", repeat: Infinity }}
      >
        {items.map((s, i) => (
          <span
            key={`${s.id}-${i}`}
            className="inline-flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.14em] text-[color:var(--muted-ink)]"
          >
            <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: s.color }} />
            {s.name}
            <span className="ml-4 text-[#C9C2B5]">/</span>
          </span>
        ))}
      </motion.div>
    </div>
  );
};

const AxisCard = ({ label, quote, description, tone, testId }) => (
  <div className="card-premium card-premium-hover p-4 sm:p-5" data-testid={testId}>
    <div className="flex items-center justify-between gap-4">
      <span className="eyebrow text-[color:var(--muted-ink)]">{label}</span>
      <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full" style={{ background: tone }} />
    </div>
    <p className="font-display mt-2.5 text-[1.15rem] leading-[1.2] tracking-[-0.01em] text-[color:var(--ink)]">“{quote}”</p>
    <p className="mt-1.5 text-[0.88rem] text-[color:var(--muted-ink)]">{description}</p>
  </div>
);

const SegmentDetail = ({ segment, onPrev, onNext, preview = false }) => (
  <div className="card-premium relative overflow-hidden p-5 sm:p-6" data-testid="segment-detail-panel">
    <motion.span
      aria-hidden="true"
      className="absolute inset-x-0 top-0 h-1.5 origin-left"
      animate={{ background: segment.color, scaleX: [0.6, 1] }}
      transition={{ background: { duration: 0.3 }, scaleX: { duration: 0.45, ease: EASE } }}
      key={`bar-${segment.id}`}
    />
    <AnimatePresence mode="wait">
      <motion.div
        key={segment.id}
        initial={{ opacity: 0, x: 14 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -10 }}
        transition={{ duration: 0.28, ease: EASE }}
      >
        <div className="flex items-center justify-between">
          <span className="eyebrow text-[color:var(--muted-ink)]">
            Segment {String(SEGMENTS.findIndex((s) => s.id === segment.id) + 1).padStart(2, "0")} of 05
            {preview ? <span className="ml-2 normal-case tracking-normal text-[#9AA5B1]">· previewing</span> : null}
          </span>
          <div className="flex items-center gap-1">
            <button type="button" onClick={onPrev} aria-label="Previous segment" data-testid="segment-prev-button" className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[color:var(--hairline)] text-[color:var(--navy)] transition-colors hover:bg-[color:var(--sand)]">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button type="button" onClick={onNext} aria-label="Next segment" data-testid="segment-next-button" className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[color:var(--hairline)] text-[color:var(--navy)] transition-colors hover:bg-[color:var(--sand)]">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
        <h3 className="font-display mt-2.5 text-[1.6rem] leading-[1.08] tracking-[-0.02em] text-[color:var(--ink)] sm:text-[1.9rem]" data-testid="segment-detail-name">
          {segment.name}
        </h3>
        <div className="mt-3 flex flex-wrap gap-2">
          <LevelChip label="Contribution" value={segment.contribution} color={segment.color} testId="segment-detail-contribution" />
          <LevelChip label="Satisfaction" value={segment.satisfaction} color={segment.color} testId="segment-detail-satisfaction" />
        </div>
        <p className="mt-4 text-pretty text-[0.98rem] leading-[1.55] text-[color:var(--ink)]" data-testid="segment-detail-summary">{segment.summary}</p>
        <p className="mt-2 text-pretty text-[0.92rem] leading-[1.55] text-[color:var(--muted-ink)]">{segment.detail}</p>
      </motion.div>
    </AnimatePresence>
  </div>
);

export const ModelSection = () => {
  const [selected, setSelected] = useState("engaged");
  const [hovered, setHovered] = useState(null);

  // The panel previews the hovered segment and falls back to the selected one.
  const shownId = hovered || selected;
  const idx = SEGMENTS.findIndex((s) => s.id === selected);
  const shown = SEGMENTS.find((s) => s.id === shownId) || SEGMENTS[idx];
  const step = (offset) => setSelected(SEGMENTS[(idx + offset + SEGMENTS.length) % SEGMENTS.length].id);
  const prev = () => step(-1);
  const next = () => step(1);

  return (
    <Section id="model" navId="model" className="bg-white hairline-top">
      <Container className="section-y">
        <ChapterHeader
          index={2}
          total={9}
          eyebrow="The model"
          title={MODEL.headline}
          testId="model-section-header"
        />

        <Reveal delay={1} className="mb-8 max-w-3xl sm:mb-10">
          {MODEL.paragraphs.map((p) => (
            <p key={p} className="text-[0.9rem] leading-[1.6] text-[color:var(--muted-ink)] sm:text-[0.92rem] [&+&]:mt-3">
              {p}
            </p>
          ))}
          <p className="mt-3 text-[0.9rem] leading-[1.6] text-[color:var(--muted-ink)] sm:text-[0.92rem]">{MODEL.segmentsIntro}</p>
          <SegmentMarquee />
        </Reveal>

        <div className="grid items-start gap-6 md:grid-cols-12 md:gap-8 lg:gap-10">
          {/* Diagram */}
          <Reveal className="md:col-span-6">
            <div className="mx-auto w-full max-w-[380px] md:max-w-none">
              <XModelImage
                selected={selected}
                hovered={hovered}
                onSelect={setSelected}
                onHover={setHovered}
                className="h-auto w-full"
                testId="model-x-model"
              />
            </div>
            <p className="mt-3 text-center font-mono text-[11px] uppercase tracking-[0.16em] text-[#7A8794]">{MATRIX.note}</p>
          </Reveal>

          {/* Detail column — beside the diagram from tablet up, directly under it on phones */}
          <div className="md:col-span-6 lg:sticky lg:top-24">
            <Reveal className="md:mt-4">
              <SegmentDetail segment={shown} onPrev={prev} onNext={next} preview={Boolean(hovered && hovered !== selected)} />
            </Reveal>

            <Reveal delay={2} className="mt-4 grid gap-3 sm:grid-cols-2">
              <AxisCard label={MODEL.satisfaction.label} quote={MODEL.satisfaction.quote} description={MODEL.satisfaction.description} tone="#F2A91E" testId="model-axis-satisfaction" />
              <AxisCard label={MODEL.contribution.label} quote={MODEL.contribution.quote} description={MODEL.contribution.description} tone="#1F6FA3" testId="model-axis-contribution" />
            </Reveal>
            <Reveal delay={3} className="mt-3 flex items-center justify-between gap-4 rounded-2xl bg-[color:var(--sand)] px-4 py-3">
              <p className="text-[0.9rem] text-[color:var(--ink)]">
                <span className="font-semibold">{MODEL.apex}</span> sits at the apex — both at their maximum.
              </p>
              <CTAButton variant="ghost" onClick={() => scrollToId("contact")} testId="model-cta-link" className="shrink-0 text-[0.88rem]">
                Understand your workforce
              </CTAButton>
            </Reveal>
          </div>
        </div>
      </Container>
    </Section>
  );
};
